/**
 * Centralized error handling utilities for the client application
 */

// Error types for better error categorization
export const ErrorTypes = {
  NETWORK: 'NETWORK_ERROR',
  AUTH: 'AUTH_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  SERVER: 'SERVER_ERROR',
  CLIENT: 'CLIENT_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

// Development mode check
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Log errors to console in development mode
 */
export const logError = (error, context = '') => {
  if (isDevelopment) {
    console.group(`🔴 Error${context ? ` in ${context}` : ''}`);
    console.error('Message:', error.message || 'Unknown error');
    console.error('Type:', error.type || ErrorTypes.UNKNOWN);
    
    if (error.response) {
      console.error('Response Status:', error.response.status);
      console.error('Response Data:', error.response.data);
    }
    
    if (error.request) {
      console.error('Request:', error.request);
    }
    
    console.error('Stack:', error.stack);
    console.groupEnd();
  }
  
  // In production, you could send errors to a logging service
  // Example: sendToLoggingService(error, context);
};

/**
 * Categorize error based on its characteristics
 */
export const categorizeError = (error) => {
  // Network errors (no response received)
  if (error.code === 'ECONNABORTED' || error.message === 'Network Error' || !error.response) {
    return ErrorTypes.NETWORK;
  }
  
  // Authentication errors
  if (error.response?.status === 401 || error.response?.status === 403) {
    return ErrorTypes.AUTH;
  }
  
  // Validation errors
  if (error.response?.status === 400 || error.response?.status === 422) {
    return ErrorTypes.VALIDATION;
  }
  
  // Server errors
  if (error.response?.status >= 500) {
    return ErrorTypes.SERVER;
  }
  
  // Client errors
  if (error.response?.status >= 400 && error.response?.status < 500) {
    return ErrorTypes.CLIENT;
  }
  
  return ErrorTypes.UNKNOWN;
};

/**
 * Get user-friendly error message
 */
export const getUserFriendlyMessage = (error) => {
  const errorType = categorizeError(error);
  
  // Check for specific error message from server
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  // Provide user-friendly messages based on error type
  switch (errorType) {
    case ErrorTypes.NETWORK:
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    
    case ErrorTypes.AUTH:
      if (error.response?.status === 401) {
        return 'Your session has expired. Please log in again.';
      }
      return 'You don\'t have permission to perform this action.';
    
    case ErrorTypes.VALIDATION:
      return error.response?.data?.errors 
        ? Object.values(error.response.data.errors).join(', ')
        : 'Please check your input and try again.';
    
    case ErrorTypes.SERVER:
      return 'Something went wrong on our end. Please try again later.';
    
    case ErrorTypes.CLIENT:
      return 'There was a problem with your request. Please try again.';
    
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

/**
 * Create a standardized error object
 */
export const createStandardError = (error, context = '') => {
  const type = categorizeError(error);
  const message = getUserFriendlyMessage(error);
  
  return {
    type,
    message,
    originalError: error,
    context,
    timestamp: new Date().toISOString(),
    // Include additional details for debugging
    details: {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      code: error.code,
    }
  };
};

/**
 * Handle API errors with logging and standardization
 */
export const handleApiError = (error, context = '') => {
  // Log the error for debugging
  logError(error, context);
  
  // Create standardized error
  const standardError = createStandardError(error, context);
  
  // Return the standardized error for the application to handle
  return standardError;
};

/**
 * Retry logic for network failures
 */
export const retryWithBackoff = async (
  fn, 
  maxRetries = 3, 
  initialDelay = 1000,
  backoffFactor = 2
) => {
  let lastError;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Only retry on network errors or 5xx server errors
      const errorType = categorizeError(error);
      if (errorType !== ErrorTypes.NETWORK && errorType !== ErrorTypes.SERVER) {
        throw error;
      }
      
      // Don't delay after the last attempt
      if (i < maxRetries - 1) {
        const delay = initialDelay * Math.pow(backoffFactor, i);
        logError(error, `Retry attempt ${i + 1}/${maxRetries} - waiting ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
};

export default {
  ErrorTypes,
  logError,
  categorizeError,
  getUserFriendlyMessage,
  createStandardError,
  handleApiError,
  retryWithBackoff
};